import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Box, Flex, Heading, Text, Button, Badge, useColorModeValue, 
  HStack, VStack, Switch, useToast, Icon, SimpleGrid
} from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';
import { useFeatureFlag, isBetaUser } from '../utils/featureFlags';
import TemplateSelector from './TemplateSelector'; // Your existing component
import SmartDesignSelector from './SmartDesignSelector'; // New smart component

const EnhancedTemplateSelector = ({ uploadedImageURL }) => {
  const [user, setUser] = useState(null);
  const [useSmartMode, setUseSmartMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  // Feature flags
  const dynamicGenerationEnabled = useFeatureFlag('DYNAMIC_DESIGN_GENERATION');
  const contentTypeSelectorEnabled = useFeatureFlag('CONTENT_TYPE_SELECTOR');

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        
        // Auto-enable smart mode for beta users
        const isUserBeta = isBetaUser(session.user.email);
        if (isUserBeta && (dynamicGenerationEnabled || contentTypeSelectorEnabled)) {
          setUseSmartMode(true);
        }
      }
      setLoading(false);
    };
    
    getCurrentUser();
  }, [dynamicGenerationEnabled, contentTypeSelectorEnabled]);

  // Check if user has access to smart features
  const hasSmartAccess = user?.email ? (
    isBetaUser(user.email) || dynamicGenerationEnabled || contentTypeSelectorEnabled
  ) : false;

  const handleModeToggle = (enabled) => {
    setUseSmartMode(enabled);
    
    // Track mode switching for analytics
    console.log(`User switched to ${enabled ? 'Smart' : 'Classic'} design mode`);
    
    toast({
      title: `Switched to ${enabled ? 'Smart' : 'Classic'} Design Mode`,
      description: enabled 
        ? 'AI will help organize and suggest the best designs for your content'
        : 'Browse all available templates in the traditional view',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };

  if (loading) {
    return (
      <Box minH="50vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Text color="gray.600" fontWeight="medium">Loading AI Designs...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" pt={4} pb={8}>
      {/* Header with Mode Toggle */}
      <Box bg="white" boxShadow="sm" mb={8} px={4} py={6}>
        <Box maxW="7xl" mx="auto">
          <VStack spacing={4} textAlign="center">
            <Box bg="gradient" bgGradient="linear(135deg, blue.500, purple.600)" p={3} borderRadius="xl">
              <Text fontSize="32px">🎨</Text>
            </Box>
            
            <Box>
              <Flex align="center" justify="center" gap={4} mb={2}>
                <Heading size="xl" color="gray.800">
                  {useSmartMode ? 'Smart AI Designs' : 'AI Designs'}
                </Heading>
                
                {/* Mode Toggle - only show if user has access */}
                {hasSmartAccess && (
                  <VStack spacing={1}>
                    <HStack spacing={3}>
                      <Text fontSize="sm" color={!useSmartMode ? "blue.600" : "gray.500"} fontWeight="medium">
                        Classic
                      </Text>
                      <Switch
                        isChecked={useSmartMode}
                        onChange={(e) => handleModeToggle(e.target.checked)}
                        colorScheme="blue"
                        size="lg"
                      />
                      <Text fontSize="sm" color={useSmartMode ? "blue.600" : "gray.500"} fontWeight="medium">
                        Smart
                      </Text>
                    </HStack>
                    <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                      Beta Feature
                    </Badge>
                  </VStack>
                )}
              </Flex>
              
              <Text color="gray.600" fontSize="lg" maxW="2xl" mx="auto">
                {useSmartMode 
                  ? 'AI-powered design suggestions based on your content type and preferences. Get the perfect layout every time.'
                  : 'Professional sports layouts that automatically fit your athlete photos. Pick a design and customize it with your images and details.'
                }
              </Text>
            </Box>
            
            <HStack spacing={4} fontSize="sm" color="gray.500">
              <Text>Step 1 of 2</Text>
              <Badge colorScheme={useSmartMode ? "purple" : "blue"} variant="subtle">
                {useSmartMode ? "AI-Powered Smart Mode" : "AI-Enhanced"}
              </Badge>
            </HStack>
          </VStack>
        </Box>
      </Box>

      <Box maxW="7xl" mx="auto" px={4}>
        {/* Mode-specific content */}
        {useSmartMode ? (
          <SmartDesignSelector 
            uploadedImageURL={uploadedImageURL} 
            user={user}
          />
        ) : (
          // Wrap existing TemplateSelector content without the header
          <Box>
            {/* How It Works Section - reuse from original */}
            <Box bg="white" borderRadius="xl" p={6} mb={8} boxShadow="sm" border="1px" borderColor="gray.200">
              <Flex align="center" mb={4}>
                <Box bg="blue.100" p={2} borderRadius="md" mr={3}>
                  <Text fontSize="20px">✨</Text>
                </Box>
                <Heading size="md" color="gray.800">How It Works</Heading>
              </Flex>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <VStack spacing={2} textAlign="center">
                  <Box bg="green.100" p={3} borderRadius="full">
                    <Text fontSize="24px">1️⃣</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">Pick a Design</Text>
                  <Text fontSize="sm" color="gray.600">Choose from professional sports layouts</Text>
                </VStack>
                <VStack spacing={2} textAlign="center">
                  <Box bg="blue.100" p={3} borderRadius="full">
                    <Text fontSize="24px">2️⃣</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">Add Your Photos</Text>
                  <Text fontSize="sm" color="gray.600">Upload athlete photos and customize text</Text>
                </VStack>
                <VStack spacing={2} textAlign="center">
                  <Box bg="purple.100" p={3} borderRadius="full">
                    <Text fontSize="24px">3️⃣</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">Get Pro Results</Text>
                  <Text fontSize="sm" color="gray.600">Download high-quality designs instantly</Text>
                </VStack>
              </SimpleGrid>
            </Box>

            {/* Use existing TemplateSelector component but without its header */}
            <TemplateSelector uploadedImageURL={uploadedImageURL} hideHeader={true} />
          </Box>
        )}

        {/* Pro Tips Section - shown for both modes but with different content */}
        <Box bg="gradient" bgGradient={useSmartMode ? "linear(135deg, purple.500, blue.600)" : "linear(135deg, blue.500, purple.600)"} 
             borderRadius="xl" p={6} mt={8} color="white">
          <Flex align="center" mb={4}>
            <Text fontSize="24px" mr={3}>💡</Text>
            <Heading size="md">
              {useSmartMode ? 'Smart Mode Tips' : 'Pro Tips for Best Results'}
            </Heading>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} fontSize="sm">
            {useSmartMode ? (
              <>
                <Text>• Smart mode analyzes your content for optimal design suggestions</Text>
                <Text>• Try different design types to see variety in layouts</Text>
                <Text>• AI learns from your selections to improve recommendations</Text>
                <Text>• Toggle back to Classic mode anytime for full template library</Text>
              </>
            ) : (
              <>
                <Text>• Use high-resolution athlete photos for crisp results</Text>
                <Text>• Portrait orientation photos work best for most designs</Text>
                <Text>• Keep backgrounds simple - the AI will enhance them</Text>
                <Text>• Try different designs to see which fits your style</Text>
              </>
            )}
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
  );
};

export default EnhancedTemplateSelector; 